/*
  Color variant selector

  Custom element behaviour for snippets/color-variant-selector.liquid. Loaded
  globally (via layout/theme.liquid and layout/quickshop.liquid) instead of from
  a snippet {% javascript %} block, so the element is also defined on collection
  and search pages. The Quick Shop modal injects the product markup (including
  <color-variant-selector>) into the CURRENT page via AJAX; the element only
  upgrades and opens if customElements.define has already run on that page.
*/
if (!customElements.get('color-variant-selector')) {
  class ColorVariantSelector extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.color-variant-selector__trigger');
      this.list = this.querySelector('.color-variant-selector__list');
      this.currentName = this.querySelector('[data-cvs-current-name]');
      this.currentThumb = this.querySelector('[data-cvs-current-thumb]');
      if (!this.trigger || !this.list) return;

      this.rows = Array.from(this.querySelectorAll('[data-cvs-row]'));
      this.radios = Array.from(this.querySelectorAll('input[data-variant-option-value]'));

      // Map each color value to its underlying (hidden) radio input.
      this.radioByValue = new Map();
      this.radios.forEach((radio) => this.radioByValue.set(radio.value, radio));

      this.variantSelection = this.closest('variant-selection');

      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.trigger.addEventListener('click', () => this.toggle(), { signal });
      this.trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event), { signal });
      this.list.addEventListener('keydown', (event) => this.onListKeydown(event), { signal });
      this.rows.forEach((row) => row.addEventListener('click', () => this.onRowClick(row), { signal }));
      document.addEventListener('pointerdown', (event) => this.onDocumentPointerdown(event), { signal });
      this.addEventListener('focusout', (event) => this.onFocusout(event), { signal });

      // Tapping a row can move focus (to nothing on iOS; to a focus-trapped
      // modal's container inside the Quick Shop / Choose Options popup) and fire
      // focusout *before* the row's click — hiding the list and swallowing the
      // selection. Flag in-component pointer interactions so onFocusout ignores
      // those blurs; auto-clear so keyboard tab-out still closes the dropdown.
      this.addEventListener('pointerdown', () => {
        this.pointerInteracting = true;
        setTimeout(() => { this.pointerInteracting = false; }, 400);
      }, { signal });

      // Re-sync the display whenever the variant changes by ANY means
      // (this row, another option, or select-first-available logic).
      if (this.variantSelection) {
        this.variantSelection.addEventListener('variant-change', () => this.sync(), { signal });
      }
      this.radios.forEach((radio) => radio.addEventListener('change', () => this.sync(), { signal }));

      this.sync();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    get isOpen() {
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    get selectableRows() {
      return this.rows.filter((row) => {
        const li = row.closest('.color-variant-selector__option');
        return !row.disabled && !(li && li.hidden);
      });
    }

    setOpen(open) {
      this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.list.hidden = !open;
    }

    open() {
      this.setOpen(true);
      const rows = this.selectableRows;
      const current = rows.find((row) => row.getAttribute('aria-current') === 'true');
      const target = current || rows[0];
      if (target) target.focus();
    }

    close(focusTrigger) {
      if (!this.isOpen) return;
      this.setOpen(false);
      if (focusTrigger) this.trigger.focus();
    }

    toggle() {
      if (this.isOpen) {
        this.close(true);
      } else {
        this.open();
      }
    }

    focusByOffset(offset) {
      const rows = this.selectableRows;
      if (!rows.length) return;
      const index = rows.indexOf(document.activeElement);
      let next = index + offset;
      if (next < 0) next = 0;
      if (next > rows.length - 1) next = rows.length - 1;
      rows[next].focus();
    }

    onRowClick(row) {
      if (row.disabled) return;
      const radio = this.radioByValue.get(row.dataset.value);
      if (!radio || radio.disabled) return;

      // Proxy to the theme's own input: this is the only thing that changes
      // the variant. <options-selection> handles everything downstream.
      if (!radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.close(true);
    }

    // Mirror the underlying radios' state onto the dropdown: which value is
    // current, which are disabled (disable-unavailable) and which are removed
    // (hidden / remove-unavailable).
    sync() {
      this.rows.forEach((row) => {
        const radio = this.radioByValue.get(row.dataset.value);
        if (!radio) return;
        const li = row.closest('.color-variant-selector__option');

        const removed = !radio.isConnected;
        if (li) li.hidden = removed;

        const disabled = radio.disabled;
        row.disabled = disabled;
        row.classList.toggle('is-unavailable', disabled);
        if (disabled) {
          row.setAttribute('aria-disabled', 'true');
        } else {
          row.removeAttribute('aria-disabled');
        }

        const isCurrent = radio.checked;
        if (li) li.classList.toggle('is-current', isCurrent);
        row.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
        if (isCurrent) {
          row.setAttribute('aria-current', 'true');
        } else {
          row.removeAttribute('aria-current');
        }
      });

      // Reflect the checked value in the closed (trigger) state.
      const checked = this.radios.find((radio) => radio.checked);
      const currentRow = checked
        ? this.rows.find((row) => row.dataset.value === checked.value)
        : null;
      if (!currentRow) return;

      if (this.currentName) {
        const name = currentRow.querySelector('.color-variant-selector__name');
        if (name) this.currentName.textContent = name.textContent;
      }
      if (this.currentThumb) {
        const thumb = currentRow.querySelector('.color-variant-selector__thumb');
        this.currentThumb.innerHTML = thumb ? thumb.innerHTML : '';
      }
    }

    onTriggerKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (this.isOpen) {
            this.focusByOffset(1);
          } else {
            this.open();
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!this.isOpen) this.open();
          {
            const rows = this.selectableRows;
            if (rows.length) rows[rows.length - 1].focus();
          }
          break;
        case 'Escape':
          this.close(true);
          break;
      }
    }

    onListKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.focusByOffset(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.focusByOffset(-1);
          break;
        case 'Home': {
          event.preventDefault();
          const rows = this.selectableRows;
          if (rows.length) rows[0].focus();
          break;
        }
        case 'End': {
          event.preventDefault();
          const rows = this.selectableRows;
          if (rows.length) rows[rows.length - 1].focus();
          break;
        }
        case 'Escape':
          event.preventDefault();
          this.close(true);
          break;
      }
    }

    onDocumentPointerdown(event) {
      if (!this.contains(event.target)) this.close(false);
    }

    onFocusout(event) {
      // Don't close while an in-component pointer interaction (tapping a row) is
      // in progress: on iOS the tapped <button> doesn't take focus, and inside a
      // focus-trapped modal focus jumps to the modal container — either way the
      // blur here would hide the list before the row's click fires and swallow
      // the selection. Outside taps still close via onDocumentPointerdown; a
      // keyboard tab-out (no preceding pointerdown) still closes here.
      if (this.pointerInteracting) return;
      if (event.relatedTarget && !this.contains(event.relatedTarget)) {
        this.close(false);
      }
    }
  }

  customElements.define('color-variant-selector', ColorVariantSelector);
}
